import React, { useState, useEffect } from 'react';
import FormField from '../../components/common/FormField';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import {
  ReplyTemplateDTO,
  ComponentPlaceholderDTO,
  LookupDTO,
  ComponentDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import Table from '../common/Table';
import {
  useComponentById,
  useComponents,
  useComponentStatuses,
} from '../../hooks/useComponentApi';

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  component: {
    id: 'component',
    title: 'Component Selection',
  },
  message: {
    id: 'message',
    title: 'Message Content',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
} as const;

interface ReplyTemplateFormProps {
  initialValues: ReplyTemplateDTO;
  onSubmit: (data: ReplyTemplateDTO) => void;
  onCancel: () => void;
}

const ReplyTemplateForm: React.FC<ReplyTemplateFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ReplyTemplateDTO>(initialValues);
  const [components, setComponents] = useState<ComponentDTO[]>([]);
  const [availablePlaceholders, setAvailablePlaceholders] = useState<
    ComponentPlaceholderDTO[]
  >([]);
  const [availableStatuses, setAvailableStatuses] = useState<LookupDTO[]>([]);
  const { data: componentsData } = useComponents();
  const { data: selectedComponentData } = useComponentById(formData.componentId || '');
  const { getStatuses: getComponentStatuses } = useComponentStatuses();

  useEffect(() => {
    setComponents(componentsData || []);
  }, [componentsData]);

  const fetchComponentData = async (componentId: string | undefined) => {
    if (!componentId) {
      setAvailablePlaceholders([]);
      setAvailableStatuses([]);
      return;
    }

    setAvailablePlaceholders(selectedComponentData?.placeholders || []);

    // Fetch statuses
    const statuses = await getComponentStatuses(componentId);
    if (statuses) {
      setAvailableStatuses(statuses);
    }
  };

  useEffect(() => {
    const initialiseData = async () => {
      // If we have an initial component, fetch its data
      if (initialValues.componentId) {
        await fetchComponentData(initialValues.componentId);
      }
    };

    initialiseData();
  }, []);

  useEffect(() => {
    if (formData.componentId) {
      setAvailablePlaceholders(selectedComponentData?.placeholders || []);
    }
  }, [formData.componentId, selectedComponentData]);

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    description: [{ required: true, message: 'Description is required' }],
    componentId: [{ required: true, message: 'Component is required' }],
    statusId: [{ required: true, message: 'Status is required' }],
    body: [{ required: false, message: 'Body is required' }],
  };

  const { errors, validate } =
    useFormValidation<ReplyTemplateDTO>(validationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  // Simplify the handleComponentChange
  const handleComponentChange = async (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    handleChange(e, setFormData);
    setFormData((prev) => ({ ...prev, statusId: undefined }));
    await fetchComponentData(e.target.value);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={validationRules.title.some((rule) => rule.required)}
          error={errors.title}
        />
        <FormField
          id="description"
          label="Description"
          type="text"
          value={formData.description || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Description"
          required={validationRules.description.some((rule) => rule.required)}
          error={errors.description}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.component.id}
        title={FORM_SECTIONS.component.title}
      >
        <FormField
          id="componentId"
          label="Component (Section)"
          type="dropdown"
          value={formData.componentId || ''}
          onChange={handleComponentChange}
          placeholder="Select Component"
          required={validationRules.componentId.some((rule) => rule.required)}
          error={errors.componentId}
          options={components.map((comp) => ({
            value: comp.id,
            label: comp.title,
          }))}
        />
        <FormField
          id="statusId"
          label="Status"
          type="dropdown"
          value={formData.statusId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Status"
          required={validationRules.statusId.some((rule) => rule.required)}
          error={errors.statusId}
          disabled={!formData.componentId}
          options={availableStatuses.map((status) => ({
            value: status.id,
            label: status.title,
          }))}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.message.id}
        title={FORM_SECTIONS.message.title}
        columns={1}
      >
        <FormField
          id="body"
          label="Message"
          type="richtext"
          value={formData.body}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Message"
          required={false}
          error={errors.body}
          helper={
            formData.componentId
              ? 'Use the placeholders listed below in your message'
              : 'Select a component to see available placeholders'
          }
        />

        {availablePlaceholders.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
                Available Placeholders
              </h3>
              <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
                {availablePlaceholders.length} placeholders
              </span>
            </div>

            <Table
              columns={[
                {
                  label: 'Placeholder',
                  key: 'tag',
                  render: (row: ComponentPlaceholderDTO) => (
                    <code className="px-2 py-1 text-sm font-mono bg-primary-50 dark:bg-primary-900 text-primary-600 dark:text-primary-300 rounded">
                      {row.tag}
                    </code>
                  ),
                },
                { label: 'Description', key: 'description' },
              ]}
              data={availablePlaceholders}
              allowPaging={false}
              isLoading={false}
            />
          </div>
        )}
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="isActive"
          label="Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Is Active"
          required={false}
          error={errors.isActive}
          helper="Check to make record active."
        />
      </FormSection>
    </Form>
  );
};

export default ReplyTemplateForm;
